// Login.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../api';
import { useGoogleLogin } from '@react-oauth/google';
import { HiEye, HiEyeOff, HiMail, HiPhone, HiFingerPrint } from 'react-icons/hi';
import { loginWithPasskey, browserSupportsWebAuthn } from '../hooks/usePasskey';

const EMAIL_DOMAINS = ['@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@icloud.com'];

/* ── Google "G" colour logo ─────────────────────────────── */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

/* ── "or" divider ───────────────────────────────────────── */
function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--gray-200)' }} />
      <span style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: 'var(--gray-200)' }} />
    </div>
  );
}

export default function Login() {
  const [method, setMethod]     = useState('email');
  const [email, setEmail]       = useState('');
  const [phone, setPhone]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [gLoading, setGLoading] = useState(false);
  const [pkLoading, setPkLoading] = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const [settings, setSettings] = useState({});
  const emailRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const referralCode = new URLSearchParams(location.search).get('ref') || '';
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || '';
    fetch(`${base}/settings/public`)
      .then(r => r.json())
      .then(d => { if (d.success) setSettings(d.settings); })
      .catch(() => {});
  }, []);

  /* ── Quick-domain helper ────────────────────────────────── */
  const applyDomain = (domain) => {
    const atIdx = email.indexOf('@');
    const base  = atIdx !== -1 ? email.slice(0, atIdx) : email;
    const next  = base + domain;
    setEmail(next);
    setTimeout(() => {
      if (emailRef.current) {
        emailRef.current.focus();
        emailRef.current.setSelectionRange(next.length, next.length);
      }
    }, 0);
  };

  /* ── Google login ───────────────────────────────────────── */
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGLoading(true);
      try {
        const res = await authAPI.googleAuth({ access_token: tokenResponse.access_token, referral_code: referralCode || undefined });
        localStorage.setItem('vg_token', res.data.token);
        localStorage.setItem('vg_user', JSON.stringify(res.data.user));
        toast.success('Signed in with Google!');
        navigate(from, { replace: true });
      } catch (err) {
        toast.error(err.response?.data?.message || 'Google sign-in failed');
      } finally {
        setGLoading(false);
      }
    },
    onError: () => toast.error('Google sign-in was cancelled'),
  });

  /* ── Passkey login ─────────────────────────────────────── */
  const handlePasskeyLogin = async () => {
    setPkLoading(true);
    try {
      const res = await loginWithPasskey(method === 'email' ? email : '');
      localStorage.setItem('vg_token', res.token);
      localStorage.setItem('vg_user', JSON.stringify(res.user));
      toast.success('Signed in with passkey!');
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Passkey sign-in failed');
    } finally {
      setPkLoading(false);
    }
  };

  /* ── Email / phone login ───────────────────────────────── */
  const handle = async (e) => {
    e.preventDefault();
    if (method === 'email' && !email.includes('@')) {
      return toast.error('Please enter a valid email address');
    }
    if (method === 'phone' && phone.replace(/\D/g, '').length < 9) {
      return toast.error('Please enter a valid phone number');
    }
    setLoading(true);
    try {
      const payload = method === 'email'
        ? { email, password }
        : { phone: '+233' + phone.replace(/^0+/, ''), password };
      const res = await authAPI.login(payload);
      localStorage.setItem('vg_token', res.data.token);
      localStorage.setItem('vg_user', JSON.stringify(res.data.user));
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || gLoading || pkLoading;
  const passkeySupported = browserSupportsWebAuthn();

  return (
    <div className="auth-page">
      <div className="auth-header">
        <div className="auth-logo">Vest<span>-Grow</span></div>
        <div className="auth-tagline">
          {settings.registration_bonus
            ? `New? Get GHS ${settings.registration_bonus} on sign-up`
            : 'Your Premium Investment Platform'}
        </div>
      </div>

      <div className="auth-body">
        <h2 style={{ marginBottom: 6, fontFamily: 'var(--font-display)', fontSize: 24 }}>Welcome Back</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 20 }}>Sign in to your account</p>

        {/* ── Google Sign-In button ──────────────────────────── */}
        <button
          type="button"
          onClick={() => googleLogin()}
          disabled={busy}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '12px 16px',
            background: 'var(--white)',
            border: '1.5px solid var(--gray-200)',
            borderRadius: 'var(--radius)',
            fontSize: 14, fontWeight: 600,
            color: 'var(--gray-800)',
            cursor: busy ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
            opacity: gLoading ? 0.7 : 1,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
          onMouseEnter={e => {
            if (!busy) {
              e.currentTarget.style.borderColor = 'var(--blue-mid)';
              e.currentTarget.style.background = 'var(--blue-pale)';
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--gray-200)';
            e.currentTarget.style.background = 'var(--white)';
          }}
        >
          {gLoading
            ? <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Signing in with Google…</span>
            : <><GoogleIcon /> Continue with Google</>}
        </button>

        {/* ── Passkey Sign-In button ─────────────────────────── */}
        {passkeySupported && (
          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={busy}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: '12px 16px',
              marginTop: 10,
              background: 'var(--white)',
              border: '1.5px solid var(--gray-200)',
              borderRadius: 'var(--radius)',
              fontSize: 14, fontWeight: 600,
              color: 'var(--gray-800)',
              cursor: busy ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              opacity: pkLoading ? 0.7 : 1,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
            onMouseEnter={e => {
              if (!busy) {
                e.currentTarget.style.borderColor = 'var(--blue-mid)';
                e.currentTarget.style.background = 'var(--blue-pale)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--gray-200)';
              e.currentTarget.style.background = 'var(--white)';
            }}
          >
            <HiFingerPrint size={20} color="var(--blue-mid)" />
            {pkLoading ? 'Waiting for biometric…' : 'Sign in with Passkey'}
          </button>
        )}

        <Divider label="or sign in with" />

        {/* ── Method toggle ─────────────────────────────────── */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          background: 'var(--gray-100)', borderRadius: 12,
          padding: 4, marginBottom: 22, gap: 4,
        }}>
          {[
            { key: 'email', label: 'Email',       Icon: HiMail  },
            { key: 'phone', label: 'Phone Number', Icon: HiPhone },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setMethod(key)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, transition: 'all 0.18s',
                background: method === key ? 'var(--white)' : 'transparent',
                color:      method === key ? 'var(--blue-mid)' : 'var(--gray-400)',
                boxShadow:  method === key ? '0 1px 6px rgba(26,58,107,0.10)' : 'none',
              }}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        <form onSubmit={handle}>

          {/* ── Email ─────────────────────────────────────────── */}
          {method === 'email' && (
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                ref={emailRef}
                className="form-control"
                type="email"
                placeholder="Type email here"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {EMAIL_DOMAINS.map(domain => (
                  <button
                    key={domain}
                    type="button"
                    onClick={() => applyDomain(domain)}
                    style={{
                      fontSize: 12, fontWeight: 600,
                      padding: '5px 10px', borderRadius: 7,
                      border: '1.5px solid',
                      borderColor: email.endsWith(domain) ? 'var(--blue-mid)' : 'var(--gray-200)',
                      background:  email.endsWith(domain) ? 'var(--blue-pale)' : 'var(--white)',
                      color:       email.endsWith(domain) ? 'var(--blue-mid)' : 'var(--gray-600)',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    {domain}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Phone ─────────────────────────────────────────── */}
          {method === 'phone' && (
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <div style={{ display: 'flex', gap: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  padding: '0 14px',
                  background: 'var(--gray-100)',
                  border: '1.5px solid var(--gray-200)',
                  borderRight: 'none',
                  borderRadius: 'var(--radius) 0 0 var(--radius)',
                  fontSize: 14, fontWeight: 700,
                  color: 'var(--blue-mid)',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  +233
                </div>
                <input
                  className="form-control"
                  type="tel"
                  placeholder="XX XXX XXXX"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                  style={{ borderRadius: '0 var(--radius) var(--radius) 0', flex: 1 }}
                  maxLength={12}
                  required
                />
              </div>
              <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 5 }}>
                Enter your number without the leading 0 — e.g. 24 123 4567
              </p>
            </div>
          )}

          {/* ── Password ──────────────────────────────────────── */}
          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-control"
                type={showPw ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ paddingRight: 44 }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--gray-400)', display: 'flex', alignItems: 'center', padding: 0,
                }}
              >
                {showPw ? <HiEyeOff size={20} /> : <HiEye size={20} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={busy} style={{ marginTop: 8 }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--gray-600)' }}>
          Don't have an account?{' '}
          <Link to={referralCode ? `/register?ref=${referralCode}` : '/register'} style={{ color: 'var(--blue-mid)', fontWeight: 600 }}>Register</Link>
        </p>

        {/* ── Footer links ───────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 24,
          marginTop: 20, paddingTop: 16,
          borderTop: '1px solid var(--gray-200)',
        }}>
          <Link to="/about" style={{ fontSize: 12, color: 'var(--gray-400)', textDecoration: 'none', fontWeight: 500 }}>
            About Us
          </Link>
          <Link to="/privacy-policy" style={{ fontSize: 12, color: 'var(--gray-400)', textDecoration: 'none', fontWeight: 500 }}>
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}